import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Banknote, Link2, RefreshCw, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METHODS = [
  { value: "cash", label: "Cash" },
  { value: "cashapp", label: "Cash App" },
  { value: "apple_pay_other", label: "Apple Pay (in person)" },
  { value: "other", label: "Other agreed method" },
];

export default function ReservationPayment({ booking, token, onSaved }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [link, setLink] = useState(null);
  const [showRecord, setShowRecord] = useState(false);
  const [method, setMethod] = useState("cash");
  const [amount, setAmount] = useState("20.00");
  const [receiptRef, setReceiptRef] = useState("");
  const [note, setNote] = useState("");
  const [ack, setAck] = useState(false);
  const [recording, setRecording] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/bookings/${booking.id}/reservation-payment-status`, auth)
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus({ required: false }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking.id, token]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <span data-testid={`reservation-payment-loading-${booking.id}`} className="text-xs text-white/35 flex items-center gap-1.5">
        <Loader2 className="animate-spin" size={13} /> Checking $20 reservation payment&hellip;
      </span>
    );
  }
  if (!status?.required) return null;

  const resendLink = async () => {
    setSending(true);
    setLink(null);
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.id}/reservation-payment-link`, {}, auth);
      setLink(data.link);
      toast.success(data.sms_sent ? "Payment request re-sent by SMS." : `Link created — SMS not sent (${data.sms_error || "no phone"}).`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the link.");
    } finally {
      setSending(false);
    }
  };

  const recordOffline = async () => {
    if (!ack) { toast.error("Confirm the policy was explained to the customer first."); return; }
    setRecording(true);
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.id}/record-reservation-payment`,
        { method, amount: Number(amount) || 0, receipt_ref: receiptRef, note, policy_acknowledged: ack }, auth);
      if (data.flagged_for_review) {
        toast.warning("Already paid elsewhere — flagged for manual review, not double-credited.");
      } else if (data.reservation_payment_status === "paid") {
        toast.success(`$20 recorded as paid (${method})${data.excess_flagged ? ` — $${data.excess_flagged} overpayment flagged for review` : ""}.`);
      } else {
        toast.success(`$${data.credited} recorded — $${data.shortfall} still needed to reach $20.`);
      }
      setShowRecord(false);
      load();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record the payment.");
    } finally {
      setRecording(false);
    }
  };

  if (status.status === "paid") {
    return (
      <div className="flex flex-wrap items-center gap-2" data-testid={`reservation-payment-${booking.id}`}>
        <span data-testid={`reservation-payment-status-${booking.id}`}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border border-emerald-500/40 text-emerald-300 bg-emerald-500/[0.08]">
          <CheckCircle2 size={12} /> $20 Reservation Payment Received ({status.method}{status.receipt_ref ? ` · ${status.receipt_ref}` : ""})
        </span>
        {status.refund_due && (
          <span data-testid={`reservation-payment-refund-due-${booking.id}`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border border-sky-400/50 text-sky-300 bg-sky-400/[0.08]">
            <Banknote size={12} /> $20 Refund Due — booking canceled, not refunded automatically
          </span>
        )}
        {status.refund_review_needed && (
          <span data-testid={`reservation-payment-refund-review-${booking.id}`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border border-white/25 text-white/70 bg-white/[0.04]">
            <Banknote size={12} /> $20 Refund Review Needed — canceled late, entitlement not assumed
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid={`reservation-payment-${booking.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span data-testid={`reservation-payment-status-${booking.id}`}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border border-amber-400/50 text-amber-300 bg-amber-400/[0.08]">
          <Banknote size={12} /> Approved — Awaiting $20 Reservation Payment
        </span>
        {status.pending_link && <span className="text-[10px] text-sky-300/80">Link active — expires {status.pending_link.expires_at?.slice(0, 16).replace("T", " ")}</span>}
        <button data-testid={`reservation-payment-resend-${booking.id}`} onClick={resendLink} disabled={sending}
          className="text-xs flex items-center gap-1.5 border border-[#F2B705]/40 text-[#F2B705] px-3 py-1.5 rounded hover:bg-[#F2B705] hover:text-black transition-colors disabled:opacity-60">
          {sending ? <Loader2 className="animate-spin" size={13} /> : status.pending_link ? <RefreshCw size={13} /> : <Link2 size={13} />}
          {status.pending_link ? "Resend Link" : "Send Payment Link"}
        </button>
        <button data-testid={`reservation-payment-record-btn-${booking.id}`} onClick={() => setShowRecord((v) => !v)}
          className="text-xs flex items-center gap-1.5 border border-white/20 text-white/70 px-3 py-1.5 rounded hover:border-[#F2B705]/50 hover:text-white transition-colors">
          <Banknote size={13} /> Record Offline Payment
        </button>
      </div>
      {(link || status.pending_link) && (
        <div data-testid={`reservation-payment-link-box-${booking.id}`} className="flex items-center gap-2 rounded-md border border-white/10 bg-black/40 p-2.5">
          <span className="text-xs text-white/60 break-all flex-1">{link || status.pending_link.url}</span>
          <button data-testid={`reservation-payment-copy-${booking.id}`} onClick={async () => {
              const url = link || status.pending_link.url;
              try { await navigator.clipboard.writeText(url); toast.success("Link copied."); }
              catch { toast.error("Could not copy to clipboard."); }
            }}
            className="text-xs flex items-center gap-1 border border-white/20 text-white/70 px-2.5 py-1.5 rounded hover:border-[#F2B705]/50 hover:text-white transition-colors shrink-0">
            <Link2 size={12} /> Copy
          </button>
        </div>
      )}
      {showRecord && (
        <div data-testid={`reservation-payment-record-form-${booking.id}`} className="rounded-lg border border-white/10 bg-black/30 p-3.5 space-y-2.5">
          <div className="grid grid-cols-2 gap-2">
            <select data-testid={`reservation-payment-method-${booking.id}`} value={method} onChange={(e) => setMethod(e.target.value)}
              className="bg-[#0C0C0C] border border-white/15 rounded px-2.5 py-1.5 text-xs text-white">
              {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            <input data-testid={`reservation-payment-amount-${booking.id}`} type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder="Amount" className="bg-[#0C0C0C] border border-white/15 rounded px-2.5 py-1.5 text-xs text-white" />
          </div>
          <input data-testid={`reservation-payment-receipt-${booking.id}`} value={receiptRef} onChange={(e) => setReceiptRef(e.target.value)}
            placeholder="Receipt / reference (optional)" className="w-full bg-[#0C0C0C] border border-white/15 rounded px-2.5 py-1.5 text-xs text-white" />
          <input data-testid={`reservation-payment-note-${booking.id}`} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Note (optional)" className="w-full bg-[#0C0C0C] border border-white/15 rounded px-2.5 py-1.5 text-xs text-white" />
          <label className="flex items-start gap-2 text-[11px] text-white/60 cursor-pointer select-none">
            <input data-testid={`reservation-payment-ack-${booking.id}`} type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)}
              className="mt-0.5 h-3.5 w-3.5 accent-[#F2B705] shrink-0" />
            <span>I confirmed with the customer that this $20 is credited toward their rental, non-refundable on no-show, and explained the cancel/transfer policy.</span>
          </label>
          <button data-testid={`reservation-payment-record-submit-${booking.id}`} onClick={recordOffline} disabled={recording || !ack}
            className="w-full text-xs font-bold bg-[#F2B705] text-black py-2 rounded hover:bg-white transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5">
            {recording ? <Loader2 className="animate-spin" size={13} /> : <CheckCircle2 size={13} />} Record Payment Received
          </button>
        </div>
      )}
      <p className="text-white/30 text-[10px]">Mandatory before booking is confirmed — credited toward the rental total, not an additional fee. No deposit, hold, or forfeiture beyond the $20 is ever created automatically.</p>
    </div>
  );
}
